// src/hooks/useWebsiteInfo.js
import React, { useMemo } from "react";
import { appName as configAppName } from "../config";
import { COMMON_WEB_PATH, DATA2_PATH } from "../constant";
import i18next from "i18next";

const useWebsiteInfo = () => {
  return useMemo(() => {
    const is911vipCC = window.location.hostname === "911vip.cc";
    const basePath = `${COMMON_WEB_PATH}/911TW/`;

    // 加語言後綴
    const withLangSuffix = (filename, extension = "webp") => {
      const langSuffix = `${filename}${i18next.language == "tw" ? "" : `_${i18next.language}`}.${extension}`;

      return langSuffix;
    };

    /**
     * @description 根據語言返回遊戲開始按鈕圖片後綴
     * tw、cn 顯示中文圖片
     * en、jp、th、tag、id、vi一律使用英文後綴
     */
    const startGameSuffix = (filename, extension = "webp") => {
      const langSuffix = `${filename}${["tw", "cn"].includes(i18next.language) ? "" : "_en"}.${extension}`;

      return langSuffix;
    };

    // 需要語言切換的圖片列表
    const langSensitiveImages = {
      // 首頁標題
      titlePOPULAR: withLangSuffix("POPULAR"),
      titleEXCLUSIVE: withLangSuffix("EXCLUSIVE"),
      titleNEW: withLangSuffix("NEW"),
      titleTOP10: withLangSuffix("TOP10"),
      titleORIGIN: withLangSuffix("ORIGIN"),

      // 提示和確認按鈕
      lineSHARE: withLangSuffix("lineSHARE"),
      hintWORD: withLangSuffix("hintWORD"),
      over18BTN: withLangSuffix("over18BTN"),
      under18BTN: withLangSuffix("under18BTN"),
      yesBTN: withLangSuffix("yesBTN2"),
      langYESbtn: withLangSuffix("langYESbtn"),
      cancelBTN: withLangSuffix("cancelBTN"),
      loginBTN: withLangSuffix("loginBTN"),
      getREAL: withLangSuffix("getREAL"),

      // 其他
      TWGameBG: withLangSuffix("TWGameBG"),
      exclusiveGameLobbyTitle: withLangSuffix(
        "exclusive/exclusiveGameLobbyTitle",
      ),
      TWGameTitle_pc: withLangSuffix("TWGameTitle_pc2"),
      modal_button: withLangSuffix("modal_button"),

      // 菜單圖
      menuHotIcon: withLangSuffix("menu/hotIcon"),
      menuHotIconActive: withLangSuffix("menu/hotIcon_active"),
      menuExclusiveIcon: withLangSuffix("menu/exclusiveIcon"),
      menuExclusiveIconActive: withLangSuffix("menu/exclusiveIcon_active"),
      menuNewIcon: withLangSuffix("menu/newIcon"),
      menuNewIconActive: withLangSuffix("menu/newIcon_active"),
      menuRightMenuBG: withLangSuffix("menu/rightMenuBG"),
      menuActiveBG: withLangSuffix("menu/activeBG"),

      // 動畫
      TW_animated: withLangSuffix("menu/TW_animated", "png"),
      TW_animated_mobile: withLangSuffix("menu/TW_animated_mobile", "png"),
      startGame: startGameSuffix("startGame2", "png"),

      // 登入
      accountTitle: withLangSuffix("accountTitle"),
      passwordTitle: withLangSuffix("passwordTitle"),
      signinBTN: withLangSuffix("signinBTN"),
      lineBTN: withLangSuffix("lineBTN"),
      balanceBG1: withLangSuffix("balanceBG1"),
      logoutBTN: withLangSuffix("logoutBTN"),
      // 投注紀錄
      betRecordICONheader: withLangSuffix("betRecordICONheader"),
      betTitle1: withLangSuffix("betTitle1"),
      orderNumber: withLangSuffix("orderNumber"),
      winLose: withLangSuffix("winLose"),
    };

    // 不需要語言切換的圖片列表
    const staticImages = {
      hintBG: "hintBG.webp",
      hintTitleBG: "hintTitleBG.webp",
      clickBoxBG: "clickBoxBG.webp",
      r18Logo: "R18_logo.webp",
      greenCheck: "greenCheck.svg",
      yesWORD: "yesWORD.webp",
      headerLeft: "headerLeft.webp",
      titleBG5: "titleBG5.webp",
      NunberBG: "NunberBG.webp",
      memberBTN: "memberBTN.webp",
      refresh: "refresh.webp",
      volumeBG: "volumeBG.webp",
      volumeTrackBG: "volumeTrackBG.webp",
      volumeTrackBG_mute: "volumeTrackBG_mute.webp",
      volumeThumbBG: "volumeThumbBG.webp",
      music: "music.webp",
      mute: "mute.webp",
      volumeICON: "volumeIcon.webp",
      muteICON: "muteIcon.webp",
      MENU_CONTAINER: "game-type-icon/MENU_CONTAINER.png",
      titleBINGO: "game-type-icon/BINGO.png",
      TOP_GAMES_BG: "TOP_GAMES_BG2x2.webp",
      TopGameTitleBG: "TopGameTitleBG.webp",
      topGameBG_mobile: "topGameBG_mobile2.webp",
      animation_BG_frame: "animation_BG_frame2x.webp",
      credit_border: "credit_border.png",
      credit_word: "credit_word.png",
      dice_animation: "dice_animation.png",
      disc_animation: "disc_animation.png",
      fish_animation: "fish_animation.png",
      game_BG: "game_BG.webp",
      searchBG: "searchBG.webp",
      PlatformBG: "PlatformBG2.webp",
      ProviderNew: "ProviderNew.webp",
      ProviderNew_clicked: "ProviderNew_clicked.webp",
      WalletICON: "WalletICON.svg",
      PrizeICON: "PrizeICON.svg",
      ThumbUpICON: "ThumbUpICON.svg",
      SharreICON: "SharreICON.svg",
      marqueeDarkBG: "marqueeDarkBG.webp",
      lngIcon: "lng_icon.png",
    };

    // 合併圖片列表
    const allImageNames = { ...langSensitiveImages, ...staticImages };

    // 處理所有圖片路徑
    const processedImages = Object.fromEntries(
      Object.entries(allImageNames).map(([key, name]) => [
        key,
        `${basePath}${name}`,
      ]),
    );

    // 首頁標題圖
    const homeTitleConfigs = {
      BINGO: {
        icon1: `${COMMON_WEB_PATH}/911TW/split.webp`,
        icon2: processedImages.titleBINGO,
      },
      TOP10: {
        icon1: `${COMMON_WEB_PATH}/911TW/split.webp`,
        icon2: processedImages.titleTOP10,
      },
      POPULAR: {
        icon1: `${COMMON_WEB_PATH}/911TW/split.webp`,
        icon2: processedImages.titlePOPULAR,
      },
      ORIGIN: {
        icon1: `${COMMON_WEB_PATH}/911TW/split.webp`,
        icon2: processedImages.titleORIGIN,
      },
      NEW: {
        icon1: `${COMMON_WEB_PATH}/911TW/split.webp`,
        icon2: processedImages.titleNEW,
      },
      EXCLUSIVE: {
        icon1: `${COMMON_WEB_PATH}/911TW/split.webp`,
        icon2: processedImages.titleEXCLUSIVE,
      },
    };

    // 動態處理圖片路徑函數
    const getDynamicImagePath = (baseFileName, extension = "webp") => {
      return `${basePath}${baseFileName}${
        i18next.language === "tw" ? "" : `_${i18next.language}`
      }.${extension}`;
    };

    // 處理首頁右側菜單圖
    const getMenuIconPath = (iconType, isActive = false) => {
      const suffix = isActive ? "_active" : "";
      return getDynamicImagePath(`menu/${iconType}Icon${suffix}`);
    };

    // PC左側選單圖
    const getPCMenuIconPath = (menuName, isActive = false) => {
      const activeSuffix = isActive ? "_active" : "";
      return getDynamicImagePath(
        `menu/${menuName.toUpperCase()}${activeSuffix}`,
      );
    };

    return {
      websiteName: configAppName,
      logoPath: `${process.env.REACT_APP_I18N_PATH}/logo/logo_144.png?ddd`,
      getImagePath: (path) => path,
      // 常用圖片路徑
      images: processedImages,
      getDynamicImagePath, // 動態圖片路徑
      getMenuIconPath, // 菜單圖路徑
      getPCMenuIconPath, // PC左側選單圖路徑
      homeTitleConfigs, // 首頁標題圖
      withLangSuffix,
      is911vipCC, // 判斷是否為911vip.cc
    };
  }, []);
};

export default useWebsiteInfo;
